import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, DollarSign, RefreshCw, BarChart3 } from 'lucide-react';
import { getGoogleAdsClient, getDateRange, type ConversionAction } from '@/lib/google-ads-client';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { useGoogleAdsCompany, GOOGLE_ADS_COMPANY_OPTIONS } from '@/hooks/useGoogleAdsCompany';

export default function ConversionTrackingPage() {
  const [conversions, setConversions] = useState<ConversionAction[]>([]);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { company, setCompany } = useGoogleAdsCompany();

  useEffect(() => { loadConversions(); }, [company]);

  const loadConversions = async () => {
    try {
      setLoading(true);
      if (!company) {
        toast.error('No company selected');
        setLoading(false);
        return;
      }
      const { data: mappings } = await supabase.from('google_ads_customer_mapping').select('*').eq('is_active', true).limit(1).maybeSingle();
      if (!mappings) { toast.error(`No account for ${company}`); return; }

      const client = getGoogleAdsClient(mappings.customer_id);
      const [actions, convMetrics] = await Promise.all([
        client.listConversionActions(mappings.customer_id),
        client.getConversionMetrics(mappings.customer_id, getDateRange(30))
      ]);

      setConversions(actions);
      setMetrics(convMetrics);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8"><div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-blue-500" /><span className="ml-3">Loading...</span></div></div>;
  }

  const totalConversions = metrics.reduce((sum, m) => sum + (m.metrics?.conversions || 0), 0);
  const totalValue = metrics.reduce((sum, m) => sum + (m.metrics?.conversions_value || 0), 0);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-end mb-4">
        <select
          value={company}
          onChange={(e) => setCompany(e.target.value as 'hales' | 'myccs' | 'abraham')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {GOOGLE_ADS_COMPANY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <h1 className="text-3xl font-bold mb-2">Conversion Tracking Hub</h1>
      <p className="text-gray-600 mb-8">Monitor all conversion actions and performance</p>

      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-gray-600">Conversion Actions</span><Target className="h-5 w-5 text-blue-500" /></div>
          <div className="text-3xl font-bold">{conversions.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-gray-600">Total Conversions</span><TrendingUp className="h-5 w-5 text-green-500" /></div>
          <div className="text-3xl font-bold text-green-600">{totalConversions}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-gray-600">Total Value</span><DollarSign className="h-5 w-5 text-purple-500" /></div>
          <div className="text-3xl font-bold text-purple-600">${totalValue.toFixed(2)}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Conversion Actions ({conversions.length})</h2></div>
        <div className="divide-y">
          {conversions.map((conv) => (
            <div key={conv.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">{conv.name}</h3>
                  <div className="flex gap-4 text-sm text-gray-600">
                    <span>Category: {conv.category}</span>
                    <span>Type: {conv.type}</span>
                    <span>Counting: {conv.countingType}</span>
                    <span className={`px-2 py-1 rounded ${conv.status === 'ENABLED' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {conv.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {conversions.length === 0 && <div className="p-12 text-center text-gray-500"><Target className="h-12 w-12 mx-auto mb-4 text-gray-400" /><p>No conversion actions found</p></div>}
      </div>
    </div>
  );
}
